'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import OtpVerifyScreen from './OtpVerifyScreen';
import ForgotPasswordFlow from './ForgotPasswordFlow';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { createClient } from '@/lib/supabase/client';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

interface LoginFormProps {
  onSwitchToSignup: () => void;
}

/** Returns a stable key scoped to the given email address */
function trustedDeviceKey(email: string) {
  return `monezite_trusted_${email.toLowerCase().trim()}`;
}

function isTrustedDevice(email: string): boolean {
  try {
    const raw = localStorage.getItem(trustedDeviceKey(email));
    if (!raw) return false;
    const { expiry } = JSON.parse(raw);
    return Date.now() < expiry;
  } catch {
    return false;
  }
}

function markDeviceTrusted(email: string) {
  try {
    const expiry = Date.now() + 30 * 24 * 60 * 60 * 1000; // 30 days
    localStorage.setItem(trustedDeviceKey(email), JSON.stringify({ expiry }));
  } catch {
    // localStorage unavailable — silently ignore
  }
}

export default function LoginForm({ onSwitchToSignup }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const { signIn, signInWithGoogle, sendOtp, verifyEmailOtp } = useAuth();
  const router = useRouter();
  const supabase = createClient();

  const { register, handleSubmit, formState: { errors } } = useForm<LoginFormData>({
    defaultValues: { remember: false },
  });

  async function onSubmit(data: LoginFormData) {
    setIsLoading(true);
    try {
      const authData = await signIn(data.email, data.password);

      // Check if the user is an admin — admins skip OTP entirely
      const userId = authData?.user?.id;
      if (userId) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('is_admin')
          .eq('id', userId)
          .single();

        if (profile?.is_admin) {
          toast.success('Welcome back!');
          router.push('/');
          router.refresh();
          return;
        }
      }

      // Skip OTP for trusted devices — go straight to the app
      if (isTrustedDevice(data.email)) {
        toast.success('Welcome back!');
        router.push('/');
        router.refresh();
        return;
      }

      // Unknown device — require OTP
      await sendOtp(data.email, false);
      setPendingEmail(data.email);
      setOtpStep(true);
      toast.success('A verification code has been sent to your email.');
    } catch (error: any) {
      const msg: string = error?.message || '';
      toast.error(msg || 'Invalid email or password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleOtpVerify(token: string) {
    await verifyEmailOtp(pendingEmail, token);
    // Mark this device as trusted so future logins skip OTP
    markDeviceTrusted(pendingEmail);
    toast.success('Welcome back!');
    router.push('/');
    router.refresh();
  }

  async function handleOtpResend() {
    await sendOtp(pendingEmail, false);
  }

  async function handleGoogleLogin() {
    setIsGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to sign in with Google. Please try again.');
      setIsGoogleLoading(false);
    }
  }

  if (forgotPassword) {
    return <ForgotPasswordFlow onBack={() => setForgotPassword(false)} />;
  }

  if (otpStep) {
    return (
      <OtpVerifyScreen
        email={pendingEmail}
        type="email"
        onVerify={handleOtpVerify}
        onResend={handleOtpResend}
        onBack={() => setOtpStep(false)}
      />
    );
  }

  return (
    <div>
      <LoadingSpinner visible={isLoading || isGoogleLoading} label={isGoogleLoading ? 'Connecting with Google…' : 'Signing in…'} />
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground mb-1">Welcome back</h1>
        <p className="text-sm text-muted-foreground">Log in to your Monezite account</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {/* Email */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="login-email">
            Email address
          </label>
          <input
            id="login-email"
            type="email"
            placeholder="you@example.com"
            className={`w-full px-4 py-3 bg-card border rounded-xl text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors duration-150 focus:border-primary focus:ring-2 focus:ring-primary/20 ${errors.email ? 'border-danger' : 'border-input'}`}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && <p className="mt-1 text-xs text-danger font-medium">{errors.email.message}</p>}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-semibold text-foreground" htmlFor="login-password">
              Password
            </label>
            <button
              type="button"
              onClick={() => setForgotPassword(true)}
              className="text-xs text-primary hover:text-cyan-600 font-medium transition-colors"
            >
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              className={`w-full px-4 py-3 pr-11 bg-card border rounded-xl text-sm text-foreground placeholder:text-muted-foreground outline-none transition-colors duration-150 focus:border-primary focus:ring-2 focus:ring-primary/20 ${errors.password ? 'border-danger' : 'border-input'}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Password must be at least 8 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="mt-1 text-xs text-danger font-medium">{errors.password.message}</p>}
        </div>

        {/* Remember me */}
        <div className="flex items-center gap-2">
          <input
            id="remember"
            type="checkbox"
            className="w-4 h-4 rounded border-input accent-primary cursor-pointer"
            {...register('remember')}
          />
          <label htmlFor="remember" className="text-sm text-muted-foreground cursor-pointer">
            Remember me for 30 days
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-primary-foreground text-sm font-semibold rounded-xl hover:bg-cyan-600 active:scale-95 transition-all duration-150 disabled:opacity-70 disabled:cursor-not-allowed"
          style={{ minHeight: '48px' }}
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Signing in...
            </>
          ) : (
            'Log In to Monezite'
          )}
        </button>
      </form>

      {/* Divider */}
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground font-medium">or continue with</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      {/* Social Auth */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <button
          type="button"
          onClick={handleGoogleLogin}
          disabled={isGoogleLoading}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-card border border-border rounded-xl text-sm font-medium text-foreground hover:bg-muted transition-colors duration-150 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {isGoogleLoading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
          )}
          Google
        </button>
        <button className="flex items-center justify-center gap-2 px-4 py-2.5 bg-card border border-border rounded-xl text-sm font-medium text-foreground hover:bg-muted transition-colors duration-150 active:scale-95">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/>
          </svg>
          GitHub
        </button>
      </div>

      {/* Switch to signup */}
      <p className="text-center text-sm text-muted-foreground">
        Don&apos;t have an account?{' '}
        <button onClick={onSwitchToSignup} className="text-primary font-semibold hover:text-cyan-600 transition-colors">
          Sign up free
        </button>
      </p>
    </div>
  );
}